import { type LegacyPayloadAdditionalData, createAnalytics } from '@source-digital/experience-analytics'
import { logger } from '../../../../../utils/logger'
import { type EmbedElementContext } from '../../../../element-embedder'
import { type EmbedTarget } from '../../../../embed-targets'
import { type MediaControls } from '../../../../media'
import { type IExperienceAdapter } from '../experience-adapter'
import { ActivationStore, ActivationTimeline, ApiActivationPopulator, type IExperienceDataProvider, createActivationContext, createExperienceAppBroker, type SimpleExperienceDataProviderData } from '@source-digital/experience-core'
import { throttle } from 'lodash-es'
import { type ExperienceActivationInstance } from 'sd-types/app/experience/activation'
import { BaseExperienceAdapter } from '../base'
import { url } from '@source-digital/experience-utils'
import { client } from '../../../../../utils/platform'
import { type Distribution, type Settings } from 'sd-types/models/distribution'
import { type ID } from 'sd-types/models'
import { type IAnalytics } from '../../analytics/analytics'
import { DIServices } from '../../../../di/services'
import { DefaultExperienceDataProviderFactory } from './data-provider-factory'
import { version } from '../../../../../../package.json'
import { experienceAppPlugin } from '@source-digital/sd-app-plugin-experience-app'
import { experienceResourceApiPlugin } from '@source-digital/sd-app-plugin-experience-api'
import { EmbedExperienceApp } from './experience-app-plugin'
import Tpl from 'nano-var-template'

const pixelAppIdTpl = import.meta.env.SOURCE_PIXEL_APP_ID ?? '{env}.ee-embed.distribution.{distribution.id}'

const appNameRenderer = Tpl({ start: '{', end: '}' })

const { getSdApiUrlFromEnv } = url

const toString = (value: number | string | undefined) => value == null ? value : String(value)

export class DirectExperienceAdapter extends BaseExperienceAdapter implements IExperienceAdapter {
  private analyticsModule: ReturnType<typeof createAnalytics>
  private dataProvider: IExperienceDataProvider | undefined
  activationTimeline: ActivationTimeline
  activationStore: ActivationStore
  private hideActivationsValue: boolean = false
  private apiBase: string
  /**
   * if this is true, it will use legacy services that prepopulates activations.
   * this will be removed in the future. Just keeping here for reference.
   */
  readonly prefetchEnabled = false
  data: Omit<SimpleExperienceDataProviderData, 'activations'>
  constructor (
    public readonly target: EmbedTarget
  ) {
    super()
  }

  #analytics: IAnalytics = {
    sendAnalytics: (event: string, payload?: any) => {
      this.analyticsModule.track(event, payload)
    },
    sendActivationAnalytics: (event: string, activation: ExperienceActivationInstance, payload?: any) => {
      this.analyticsModule.track(event, { activation, ...payload })
    }
  }

  analytics (): IAnalytics {
    return this.#analytics
  }

  get ctx (): EmbedElementContext {
    return this.target.ctx
  }

  get mediaControl (): MediaControls | undefined {
    return this.target.mediaControl
  }

  async #getDataProvider (): Promise<IExperienceDataProvider> {
    const factory = this.ctx.di.get(DIServices.ExperienceDataProviderFactory) ?? new DefaultExperienceDataProviderFactory({ static: !this.prefetchEnabled })
    return await factory.create(this.ctx)
  }

  async #syncData (): Promise<void> {
    if (this.dataProvider == null) {
      logger.error('dataProvider is missing')
      return
    }
    this.data = {
      distribution: await this.dataProvider.getDistribution(),
      settings: await this.dataProvider.getSettings(),
      tags: await this.dataProvider.getTags(),
      organization: await this.dataProvider.getOrganization()
    }
  }

  async initialize (): Promise<void> {
    if (this.ctx.distributionId == null) {
      logger.error('distributionId is missing')
      return
    }
    const apiBase = getSdApiUrlFromEnv(this.ctx.settings.env)
    if (apiBase == null) {
      logger.error('apiBase could not be determined')
      return
    }
    this.apiBase = apiBase

    this.dataProvider = await this.#getDataProvider()

    await this.#syncData()
    const { distribution, settings, organization } = this.data

    const { eeAppPlugin, getUid } = await this.initAppBroker(apiBase, settings)
    this.analyticsModule = this.createAnalytics(settings, distribution, organization, getUid)
    await eeAppPlugin?.pluginCtx?.eventEmitter?.emit('boot')

    if (this.mediaControl != null) { this.setupAnalytics(this.mediaControl) }

    await this.initActivationTimeline(apiBase, this.dataProvider)

    // hook up activation timeline
    this.mediaControl?.on('timeupdate', throttle(() => {
      this.onTimeUpdate()
    }, this.ctx.settings.direct?.options?.activationUpdateThrottleMs ?? 200))

    await this.onReadyHandler()
    await eeAppPlugin?.pluginCtx?.eventEmitter?.emit('hydrated')
  }

  hideActivations (hide: boolean): void {
    this.hideActivationsValue = hide
    this.onTimeUpdate()
  }

  private onTimeUpdate () {
    if (this.hideActivationsValue) {
      this.onCurrentActivationsUpdateHandler([])
      return
    }
    const { distribution, settings, tags } = this.data
    const currentTimeMsec = (this.mediaControl?.currentTime ?? 0) * 1000
    const currentItems = this.activationTimeline.getAt(currentTimeMsec).map((activation) => ({
      activation,
      context: createActivationContext(activation, {
        settings,
        currentTimeMsec,
        isPreview: true,
        tags,
        distribution,
        apiBaseUrl: this.apiBase
      })
    }))
    this.onCurrentActivationsUpdateHandler(currentItems)
  }

  private async initActivationTimeline (apiBase: string, dataProvider: IExperienceDataProvider) {
    const activations = await dataProvider.getActivations()
    const activationStore = new ActivationStore(new ApiActivationPopulator(apiBase))
    await activationStore.set(activations ?? [])

    const duration = this.mediaControl?.duration

    const timeline = new ActivationTimeline(activationStore.items, { duration })
    activationStore.on('change', (items) => {
      timeline.clear()
      timeline.duration = this.mediaControl?.duration ?? timeline.DEFAULT_DURATION
      timeline.set(items)
    })
    this.mediaControl?.on('durationchange', () => {
      timeline.duration = this.mediaControl?.duration ?? timeline.DEFAULT_DURATION
      timeline.reevaluate()
    })
    this.activationTimeline = timeline
    this.activationStore = activationStore
  }

  private createAnalytics (settings: Settings | undefined, distribution: Pick<Distribution, 'name' | 'id'>, organization: { id: ID } | undefined, getUid: () => Promise<string>) {
    const env = this.ctx.settings.env ?? 'prod'
    let uid
    const pixelAppId = appNameRenderer(pixelAppIdTpl, { distribution, env })
    return createAnalytics({
      app: 'ee-embed-direct',
      version,
      debug: false,
      getSettings: () => settings ?? {},
      pluginOptions: {
        sourcePixel: {
          logger: logger.debug.bind('source-pixel'),
          waitForPromise: new Promise((resolve, reject) => {
            getUid().then(value => {
              uid = value
              resolve({
                pixel: {
                  id: pixelAppId,
                  uid,
                  transport: 'image'
                }
              })
            }).catch(err => { reject(err) })
          }),
          getAdditionalData: () => {
            return {
              // utmParams,
              env,
              sessionId: this.ctx.id,
              agent: client,
              isMobile: client.is?.mobile === true,
              distribution: {
                id: toString(distribution.id) as string,
                name: toString(distribution.name) as string
              },
              isInteractive: false,
              organizationId: toString(organization?.id),
              isAutoplay: false,
              pixelId: uid,
              videoUrl: this.mediaControl?.currentSrc ?? ''
            } satisfies LegacyPayloadAdditionalData
          }
        },
        urls: {}
      }
    })
  }

  private async initAppBroker (apiBase: string, settings?: Settings) {
    const eeApp = new EmbedExperienceApp(this)
    const eeAppPlugin = experienceAppPlugin({ api: eeApp })
    const { broker, getUid } = createExperienceAppBroker({
      eeApiPlugin: experienceResourceApiPlugin({
        baseUrl: apiBase
      }),
      eeAppPlugin,
      settings
    })
    await broker.initialize()
    return {
      broker,
      eeAppPlugin,
      eeApp,
      getUid
    }
  }
}
